/**
 * Stage 37 — the USER-FACING drawer contract for scenarios 6–7.
 *
 * WHY THIS FILE EXISTS
 * The scenarios 6–7 drawer used to render the candidate catalog directly: cluster ids,
 * ranks, candidate counts, raw SHAP contributions, model versions and four repeated
 * model caveats. That is the pipeline's vocabulary, not the analyst's. This module turns
 * one `CandidatePairDetailData` into a plain view model that answers four questions —
 * why look at this, why is it unusual, why might the two shipments be linked, what should
 * be checked — and pushes everything technical into one collapsed block.
 *
 * RULES THIS MODULE ENFORCES (the component only lays the result out)
 *  • Nothing is invented. Every number and identifier comes from the payload; there are
 *    no baselines, corpus sizes, counts or multipliers for 6–7 because the payload
 *    carries none. A reason with no data degrades to a sentence without numbers, never
 *    to a fabricated one.
 *  • Scores, levels, SHAP values and reason codes are read, never recomputed. The level
 *    vocabularies are IMPORTED from the scenarios 1–5 contract so the two surfaces cannot
 *    call `priority_review` different things.
 *  • An absent value produces NO row. «Нет данных» and «Поле отсутствует в источнике»
 *    are catalog diagnostics; they stay in the technical block.
 *  • `match_score` is never shown as a probability or a percentage in the user part —
 *    only as one neutral sentence, with the number itself left to the technical block.
 *  • Different transport units are stated neutrally: scenarios 6–7 exist precisely to
 *    look for links ACROSS units, so a wagon change is not a failed check.
 */
import {
  anomalyLevelLabels,
  mlLevelLabels,
  type AnomalyLevel,
  type MlLevel,
} from '../anomaly-cases-v3.types'
import { formatWeightDifference } from '../weight-format'
import { formatDay } from './cross-unit.presenters'
import type { EvidenceRow } from '../evidence-row'
import {
  NO_EVIDENCE_TEXT,
  renderEvidenceReasons,
  type AnomalyExplanationBlock,
  type ReasonSubjectContext,
} from './cross-unit.reason-evidence'
import {
  resolveCaseClassification,
  type EtsngCaseClassification,
} from '@/lib/etsng-classification'
import type {
  CandidateExplanationsData,
  CandidatePairDetailData,
  LegDetail,
} from './cross-unit.types'

import type { Formatter, TranslateFn } from '@/lib/i18n'
/* ── view model ───────────────────────────────────────────────────────────── */

export interface CaseFact {
  key: string
  label: string
  value: string
}

export interface CaseReason {
  code: string
  title: string
  text: string
  /** The numbers behind the conclusion, rendered as a table under it (Stage 39). */
  evidence: EvidenceRow[]
  note?: string | null
}

/** One linkage statement. `match` = the two legs agree; `neutral` = context, not a defect. */
export interface CaseLinkageItem {
  key: string
  text: string
  tone: 'match' | 'neutral'
}

export interface CaseLeg {
  title: string
  rows: CaseFact[]
}

export interface CrossUnitCaseView {
  header: { title: string; shortId: string; fullId: string; scenarioId: number }
  notice: string
  attention: {
    anomaly: { label: string; value: string; levelLabel: string } | null
    ml: { label: string; value: string; levelLabel: string } | null
    summary: string
  }
  unusual: { reasons: CaseReason[]; emptyNote: string | null }
  linkage: { items: CaseLinkageItem[]; strengthNote: string | null }
  legs: { importLeg: CaseLeg; exportLeg: CaseLeg; middle: CaseFact[] }
  checklist: string[]
  details: CaseFact[]
  /**
   * Классификация ЕТСНГ груза — справочные уровни над кодом, не признак и не причина.
   *
   * Считается из кодов ОБЕИХ ног (`importLeg.cargoEtsng` / `exportLeg.cargoEtsng`),
   * поэтому здесь, в отличие от карточки 1–5, разделение на ноги настоящее, а не
   * выведенное из флага. У всех 2 332 916 пар каталога коды ног равны, так что на
   * практике это `shared`; ветка `perLeg` существует, потому что правило сопоставления
   * может измениться, а карточка не должна тогда склеивать разные грузы.
   */
  cargoClassification: EtsngCaseClassification
  /**
   * Расхождения, которые нельзя чинить догадкой, — печатаются рядом с фактами.
   * Пусто, когда все показанные величины сходятся между собой.
   */
  dataQuality: string[]
  technical: CaseFact[]
}

/* ── the single caveat (Stage 37) ─────────────────────────────────────────── */

/**
 * ONE sentence for the whole drawer. It replaces four separate texts that said the same
 * thing in four places («связь не подтверждена физически», «сила связи — не вероятность»,
 * «Track A и аномалия — разные модели», «атрибуты отправки описательны»). The precise
 * catalog semantics those carried are preserved verbatim in the technical block.
 */
export const crossUnitNotice = (t: TranslateFn): string => t('analytics.cu.notice')

/** Candidate semantics, moved out of the user part into «Технические сведения». */
export const candidateSemanticsTech = (t: TranslateFn): string =>
  t('analytics.cu.candidateSemantics')

/* ── value helpers ────────────────────────────────────────────────────────── */

/**
 * A leg field as a user-facing string, or null.
 *
 * `'0'` is treated as absent on purpose: the catalog stores a placeholder zero for a
 * missing wagon number (observed on container-only legs), and printing «Вагон 0» would
 * read as a real unit. Numeric zero is NOT filtered — a real 0 tonnes is a value.
 */
function legText(leg: LegDetail | null | undefined, field: string): string | null {
  if (!leg || leg.available === false) return null
  const raw = leg[field]
  if (raw === null || raw === undefined) return null
  const s = String(raw).trim()
  if (s === '' || s === '0') return null
  return s
}

function legNumber(leg: LegDetail | null | undefined, field: string): number | null {
  if (!leg || leg.available === false) return null
  const raw = leg[field]
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}

/**
 * «РОССИЯ» → «Россия». Source countries are stored upper-case.
 *
 * i18n: локаль 'ru-RU' здесь НАМЕРЕННО захардкожена и не заменяется на
 * f.dateFns/локаль интерфейса. Это нормализация РЕГИСТРА кириллических имён
 * стран из источника, а не форматирование под язык интерфейса: результат
 * сравнивается в sameText() ниже. Турецкая/казахская локаль дала бы другое
 * отображение i/İ и рассинхронизировала бы сравнение с эталоном.
 */
function countryText(v: string | null): string | null {
  if (!v) return null
  const s = v.trim()
  if (!s) return null
  return s.charAt(0).toUpperCase() + s.slice(1).toLocaleLowerCase('ru-RU')
}

/**
 * i18n: 'ru-RU' захардкожена НАМЕРЕННО — это приведение регистра для
 * СРАВНЕНИЯ строк (a === b), а не вывод на экран. Смена локали изменила бы
 * логику сравнения (правила регистра различаются между локалями), а не
 * язык интерфейса.
 */
function sameText(a: string | null, b: string | null): boolean {
  return !!a && !!b && a.trim().toLocaleUpperCase('ru-RU') === b.trim().toLocaleUpperCase('ru-RU')
}

/**
 * i18n: число уходит в `count` СЫРЫМ — движок выбирает форму через Number(count),
 * и уже отформатированная строка дала бы NaN и всегда последнюю форму.
 */
function daysText(n: number | null, t: TranslateFn): string | null {
  return n === null ? null : t('analytics.cu.days', { count: n })
}

/** Adds a fact only when the value exists — Phase 9's hide-empty rule in one place. */
function push(list: CaseFact[], key: string, label: string, value: string | null | undefined): void {
  if (value === null || value === undefined) return
  const s = String(value).trim()
  if (!s) return
  list.push({ key, label, value: s })
}

/**
 * Transport unit of one leg. A leg can carry a wagon, a container or both
 * (`unit_type = wagon_and_container`), so both are stated when both exist.
 */
export function legUnitText(leg: LegDetail | null | undefined, t: TranslateFn): string | null {
  const wagon = legText(leg, 'wagonNumber')
  const container = legText(leg, 'containerNumber')
  if (wagon && container) return t('analytics.cu.unit.wagonAndContainer', { wagon, container })
  if (wagon) return t('analytics.cu.unit.wagon', { wagon })
  if (container) return t('analytics.cu.unit.container', { container })
  return null
}

/* ── плательщик ───────────────────────────────────────────────────────────── */

/**
 * Коды плательщика, которые НЕ идентифицируют плательщика.
 *
 * `'0'` — заполнитель каталога: он стоит там, где плательщик в накладной не
 * назван (в паре с ним источник хранит название «УСЛОВНЫЙ»). Историческая
 * активность по такому коду — это активность ВСЕХ безымянных отправок, а не
 * одного контрагента, поэтому связывать её с кейсом фразой «этот плательщик»
 * нельзя. Значение при этом не подменяется: сырой код уходит в технические
 * сведения как есть.
 */
const PAYER_PLACEHOLDER_CODES = new Set(['0', '00', '000', '0000'])

export type PayerSide = 'import' | 'export'

export interface PayerIdentity {
  side: PayerSide
  /** Код источника без изменений, включая заполнитель. */
  rawCode: string | null
  /** Код, пригодный для однозначной привязки; `null` у заполнителя. */
  code: string | null
  name: string | null
  identified: boolean
}

/**
 * Плательщик одной ноги.
 *
 * Читает поля НАПРЯМУЮ, а не через `legText`: тот гасит `'0'` как заполнитель
 * номера вагона, и код плательщика вместе с ним исчезал из карточки целиком —
 * ни кода, ни названия, ни причины. Здесь заполнитель наоборот сохраняется и
 * помечается.
 */
export function readPayer(leg: LegDetail | null | undefined, side: PayerSide): PayerIdentity {
  const raw = leg && leg.available !== false ? leg.payerCode : null
  const rawCode = raw === null || raw === undefined ? null : String(raw).trim() || null
  const nameRaw = leg && leg.available !== false ? leg.payerName : null
  const name = nameRaw === null || nameRaw === undefined ? null : String(nameRaw).trim() || null
  const identified = !!rawCode && !PAYER_PLACEHOLDER_CODES.has(rawCode)
  return { side, rawCode, code: identified ? rawCode : null, name, identified }
}

/**
 * «ТОО «…» · код 2781992» / «Название не найдено · код 2781992» /
 * «Не установлен · в источнике код 0, название «УСЛОВНЫЙ»».
 *
 * `null` — в источнике нет ни кода, ни названия: строки быть не должно (общее
 * правило карточки — отсутствующее значение не рисуется).
 */
export function payerText(p: PayerIdentity, t: TranslateFn): string | null {
  if (p.identified) {
    return p.name
      ? t('analytics.cu.payer.named', { name: p.name, code: p.code ?? '' })
      : t('analytics.cu.payer.nameNotFound', { code: p.code ?? '' })
  }
  if (!p.rawCode) {
    return p.name ? t('analytics.cu.payer.unsetNameOnly', { name: p.name }) : null
  }
  // Заполнитель: показываем ровно то, что лежит в источнике, и говорим, что это не он.
  return p.name
    ? t('analytics.cu.payer.unsetCodeAndName', { code: p.rawCode, name: p.name })
    : t('analytics.cu.payer.unsetCode', { code: p.rawCode })
}

/* ── даты и интервал ──────────────────────────────────────────────────────── */

/** ISO-день, если поле разбирается в дату; иначе `null`. Ничего не достраиваем. */
function isoDay(v: unknown): string | null {
  const s = String(v ?? '').trim()
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}

function diffDays(from: string | null, to: string | null): number | null {
  if (!from || !to) return null
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return Math.round((b - a) / 86_400_000)
}

export interface PairIntervalView {
  /** Значение признака, как его отдал каталог. Не пересчитывается. */
  days: number | null
  /** «16.01.2022 → 06.02.2022» — даты отправок, которые видит аналитик. */
  shipmentDatesText: string | null
  /** Даты, по которым сходится интервал, когда это НЕ даты отправок. */
  intervalDatesLabel: string | null
  intervalDatesText: string | null
  /** Разница между показанными датами отправок, если её удалось посчитать. */
  shipmentDiffDays: number | null
  status: 'confirmed_by_shipment_dates' | 'confirmed_by_pairing_dates' | 'unconfirmed' | 'unknown'
  warning: string | null
}

/**
 * Откуда берётся интервал и совпадает ли он с датами на экране.
 *
 * ПОЧЕМУ ЭТО ВООБЩЕ НУЖНО. Карточка показывала «Даты 16.01.2022 → 06.02.2022» и
 * «Интервал 5 дней» — между этими датами 21 день. Обе величины верны и обе из
 * источника: даты отправок — это `acceptance_date` обеих ног, а интервал пары
 * (Stage 23) считается от даты РАСКРЕДИТОВАНИЯ импортной ноги (`releaseDate`,
 * с откатом на дату приёма на границе) до даты отправления экспортной. Это
 * разные события, и подписаны они должны быть по-разному.
 *
 * Значение интервала здесь НЕ меняется ни при каком исходе: если ни одна пара
 * дат не сходится с признаком, карточка честно говорит, что интервал не
 * подтверждён, вместо того чтобы назвать основанием даты, которые дают другое
 * число.
 */
export function buildPairInterval(detail: CandidatePairDetailData, t: TranslateFn): PairIntervalView {
  const imp = detail.importLeg
  const exp = detail.exportLeg
  const days = typeof detail.pairing?.daysBetween === 'number' ? detail.pairing.daysBetween : null

  const impShip = isoDay(legText(imp, 'acceptanceDate'))
  const expShip = isoDay(legText(exp, 'acceptanceDate'))
  const shipmentDatesText = impShip && expShip ? `${formatDay(impShip, t)} → ${formatDay(expShip, t)}` : null
  const shipmentDiffDays = diffDays(impShip, expShip)

  // Порядок отката повторяет Stage 23: COALESCE(credit_date, accept_date, …).
  const impRelease = isoDay(legText(imp, 'releaseDate'))
  const impBorder = isoDay(legText(imp, 'borderAcceptDate'))
  const impPair = impRelease ?? impBorder
  const impPairLabel = impRelease
    ? t('analytics.cu.interval.basisRelease')
    : impBorder ? t('analytics.cu.interval.basisBorder') : null
  const pairDiff = diffDays(impPair, expShip)

  const empty: PairIntervalView = {
    days,
    shipmentDatesText,
    intervalDatesLabel: null,
    intervalDatesText: null,
    shipmentDiffDays,
    status: 'unknown',
    warning: null,
  }
  if (days === null) return empty
  if (shipmentDiffDays === days) return { ...empty, status: 'confirmed_by_shipment_dates' }
  if (pairDiff === days && impPair && expShip) {
    return {
      ...empty,
      status: 'confirmed_by_pairing_dates',
      intervalDatesLabel: t('analytics.cu.interval.datesLabel'),
      intervalDatesText: t('analytics.cu.interval.datesText', {
        basis: impPairLabel ?? '',
        from: formatDay(impPair, t),
        to: formatDay(expShip, t),
      }),
    }
  }
  return {
    ...empty,
    status: 'unconfirmed',
    warning: shipmentDiffDays === null
      ? t('analytics.cu.interval.unconfirmedNoDates', { days: daysText(days, t) ?? '' })
      : t('analytics.cu.interval.unconfirmedMismatch', {
          shown: daysText(shipmentDiffDays, t) ?? '',
          days: daysText(days, t) ?? '',
        }),
  }
}

function scoreOutOfTen(v: number | null | undefined, t: TranslateFn): string | null {
  return typeof v === 'number' && Number.isFinite(v)
    ? t('analytics.cu.scoreOutOfTen', { value: v.toFixed(2) })
    : null
}

/**
 * Level → analyst wording. Both vocabularies come from the scenarios 1–5 contract; an
 * unknown level falls back to the raw key rather than being hidden.
 */
export function anomalyLevelText(level: string | null | undefined, t: TranslateFn): string {
  const k = String(level ?? '').trim()
  return anomalyLevelLabels(t)[k as AnomalyLevel] ?? (k || '—')
}

export function mlLevelText(level: string | null | undefined, t: TranslateFn): string {
  const k = String(level ?? '').trim()
  const ml = mlLevelLabels(t)
  if (k === 'priority_review') return anomalyLevelLabels(t).priority_review
  if (k === 'elevated') return ml.medium
  if (k === 'baseline') return ml.low
  return ml[k as MlLevel] ?? (k || '—')
}

const HIGH_ANOMALY = new Set(['priority_review', 'high'])

/* ── 1. «Почему обратить внимание» ────────────────────────────────────────── */

/**
 * The conclusion is derived from the two LEVELS, not from the numbers, and it never
 * claims a score is a probability of wrongdoing.
 */
export function buildAttentionSummary(
  anomalyLevel: string | null,
  mlLevel: string | null,
  t: TranslateFn,
): string {
  const unusual = HIGH_ANOMALY.has(String(anomalyLevel ?? ''))
  const priority = String(mlLevel ?? '') === 'high'
  // Stage 38: «похожих связей» named nothing, so the group is stated outright — the
  // anomaly model scores all of scenarios 6–7 as ONE cohort, which is what «другие связи
  // сценариев 6–7» refers to.
  if (unusual && priority) return t('analytics.cu.attention.unusualAndPriority')
  if (unusual) return t('analytics.cu.attention.unusual')
  if (priority) return t('analytics.cu.attention.priority')
  return t('analytics.cu.attention.neither')
}

/* ── 2. «Почему этот кейс необычен» ───────────────────────────────────────── */

/**
 * Stage 38: reasons are QUANTITATIVE or they are not shown.
 *
 * The previous version mapped each reason code to a fixed sentence («модель отметила
 * сочетание импортного и экспортного груза как редкое среди похожих связей»). It read like
 * a finding while carrying no number, no baseline and no named comparison group — the
 * analyst could neither check it nor weigh it.
 *
 * Now every card is built from `anomalyExplanation`, the structured evidence the detail API
 * returns: the feature that fired, its value here, the median and quartiles of that feature
 * over the anomaly model's own train population, and the share of that population above or
 * below the value. A code with no usable evidence produces NO card — it stays in the
 * technical block. Nothing is narrated that the snapshot cannot support.
 */
export const MAX_REASONS = 3

export function buildUnusualReasons(
  f: Formatter,
  explanation: AnomalyExplanationBlock | null | undefined,
  subjects?: ReasonSubjectContext,
): CaseReason[] {
  return renderEvidenceReasons(f, explanation, subjects)
    .slice(0, MAX_REASONS)
    .map((r) => ({ code: r.code, title: r.title, text: r.text, evidence: r.evidence, note: r.note }))
}

/** Checklist lines carrying the same numbers as the reason cards. */
export function buildEvidenceChecklist(
  f: Formatter,
  explanation: AnomalyExplanationBlock | null | undefined,
  subjects?: ReasonSubjectContext,
): string[] {
  return renderEvidenceReasons(f, explanation, subjects).slice(0, MAX_REASONS).map((r) => r.checklistText)
}

/**
 * Идентификаторы, которыми ключуются причины.
 *
 * Пока такой ключ один — плательщик, и он берётся со стороны ИМПОРТА: именно
 * `import_payer_code` Stage 24 кладёт в семейство `payer`, и по нему же
 * отбираются строки drill-down. Сторона зашита здесь, а не в тексте причины,
 * чтобы её нельзя было перепутать в одном месте и не перепутать в другом.
 */
export function buildReasonSubjects(
  detail: CandidatePairDetailData,
  /**
   * Какое семейство участников сейчас объясняется. Stage 24 пробует `exp_participant`
   * полем `export_sender_bin`, а `imp_participant` — полем `import_receiver_bin`; взять
   * не ту ногу значит назвать в причине НЕ ТО лицо, по которому считалось число.
   */
  participantFamily?: 'imp_participant' | 'exp_participant' | null,
): ReasonSubjectContext {
  const p = readPayer(detail.importLeg, 'import')
  const out: ReasonSubjectContext = {
    payer: { side: 'import', code: p.code, name: p.name, identified: p.identified, rawCode: p.rawCode },
  }
  if (participantFamily) {
    const side = participantFamily === 'exp_participant' ? 'export' : 'import'
    const role = participantFamily === 'exp_participant' ? 'sender' : 'receiver'
    const leg = side === 'export' ? detail.exportLeg : detail.importLeg
    const ok = !!leg && leg.available !== false
    const rawBin = ok ? (role === 'sender' ? leg.senderBin : leg.receiverBin) : null
    const rawName = ok ? (role === 'sender' ? leg.senderName : leg.receiverName) : null
    const bin = rawBin === null || rawBin === undefined ? null : String(rawBin).trim() || null
    const name = rawName === null || rawName === undefined ? null : String(rawName).trim() || null
    out.participant = { side, role, bin, name, identified: !!bin }
  }
  return out
}

/**
 * Какое семейство участников фигурирует в причинах кейса, по признаку, который сработал.
 *
 * Читается из `feature` объяснения, а не из кода причины: `UNUSUAL_PARTICIPANT_HANDOFF`
 * поднимается и импортным, и экспортным семейством, и по коду сторону не восстановить.
 * Две разные стороны в одном кейсе — редкий случай; тогда участник не подставляется
 * вовсе, потому что одна подпись не может назвать обоих.
 */
export function participantFamilyOf(
  explanation: AnomalyExplanationBlock | null | undefined,
): 'imp_participant' | 'exp_participant' | null {
  const fams = new Set(
    (explanation?.reasons ?? [])
      .map((r) => (String(r.feature ?? '').startsWith('exp_participant_') ? 'exp_participant'
        : String(r.feature ?? '').startsWith('imp_participant_') ? 'imp_participant' : null))
      .filter((f): f is 'imp_participant' | 'exp_participant' => f !== null),
  )
  return fams.size === 1 ? [...fams][0] : null
}

/* ── 3. «Почему отправки могут быть связаны» ──────────────────────────────── */

/**
 * Rule-match magnitude → one neutral sentence. The number itself is NOT shown here: 95.67
 * next to a linkage list reads as «95% вероятности», which is exactly what `match_score`
 * is not. It stays in the technical block.
 */
export function matchStrengthNote(matchScore: number | null | undefined, t: TranslateFn): string | null {
  if (typeof matchScore !== 'number' || !Number.isFinite(matchScore)) return null
  // Целые фразы, а не склоняемые фрагменты: в казахском такого «слота» нет.
  if (matchScore >= 80) return t('analytics.cu.match.high')
  if (matchScore >= 50) return t('analytics.cu.match.medium')
  return t('analytics.cu.match.low')
}

/**
 * Оговорка о разных транспортных единицах.
 *
 * Сценарии 6–7 существуют ровно для того, чтобы искать связь МЕЖДУ разными
 * единицами, поэтому смена вагона или контейнера — не провалившаяся проверка.
 * Но и подтверждением перегрузки она не является: система сопоставила отправки
 * по совокупности признаков, а физическая передача груза остаётся предметом
 * документальной проверки.
 */
export const differentUnitsNote = (scenarioId: number, t: TranslateFn): string =>
  t('analytics.cu.link.differentUnitsNote', { id: scenarioId })

export function buildLinkage(
  detail: CandidatePairDetailData,
  scenarioId: number,
  t: TranslateFn,
): { items: CaseLinkageItem[]; strengthNote: string | null } {
  const imp = detail.importLeg
  const exp = detail.exportLeg
  const items: CaseLinkageItem[] = []

  // cargo — GNG first (the more specific code), ЕТСНГ as the fallback
  const gngI = legText(imp, 'cargoGng')
  const gngE = legText(exp, 'cargoGng')
  const etsngI = legText(imp, 'cargoEtsng')
  const etsngE = legText(exp, 'cargoEtsng')
  if (sameText(gngI, gngE)) {
    items.push({ key: 'cargo', text: t('analytics.cu.link.sameCargoGng', { code: gngI ?? '' }), tone: 'match' })
  } else if (sameText(etsngI, etsngE)) {
    items.push({ key: 'cargo', text: t('analytics.cu.link.sameCargoEtsng', { code: etsngI ?? '' }), tone: 'match' })
  } else if (gngI && gngE) {
    items.push({ key: 'cargo', text: t('analytics.cu.link.differentCargoGng', { from: gngI, to: gngE }), tone: 'neutral' })
  }

  // hand-over station: import destination = export origin
  const stIn = legText(imp, 'destinationStationName')
  const stOut = legText(exp, 'departureStationName')
  if (sameText(stIn, stOut)) {
    items.push({ key: 'station', text: t('analytics.cu.link.sameStation', { station: stIn ?? '' }), tone: 'match' })
  } else if (stIn && stOut) {
    items.push({ key: 'station', text: t('analytics.cu.link.differentStations', { from: stIn, to: stOut }), tone: 'neutral' })
  }

  // participants: import receiver = export sender
  const binIn = legText(imp, 'receiverBin')
  const binOut = legText(exp, 'senderBin')
  if (sameText(binIn, binOut)) {
    items.push({
      key: 'bin',
      text: t('analytics.cu.link.sameBin', { bin: binIn ?? '' }),
      tone: 'match',
    })
  }

  // payer — сравниваются только установленные коды: заполнитель «0» совпадает
  // сам с собой у половины каталога и признаком связи не является.
  const payIn = readPayer(imp, 'import')
  const payOut = readPayer(exp, 'export')
  if (payIn.identified && payOut.identified) {
    if (sameText(payIn.code, payOut.code)) {
      items.push({ key: 'payer', text: t('analytics.cu.link.samePayer', { code: payIn.code ?? '' }), tone: 'match' })
    } else {
      items.push({
        key: 'payer',
        text: t('analytics.cu.link.differentPayers', { from: payIn.code ?? '', to: payOut.code ?? '' }),
        tone: 'neutral',
      })
    }
  } else if (payIn.rawCode || payOut.rawCode) {
    // Целые предложения на каждый исход: склоняемый фрагмент «у импортной отправки»
    // в казахском в такой слот не встаёт.
    const text = !payIn.identified && !payOut.identified
      ? t('analytics.cu.link.payerUnsetBoth')
      : !payIn.identified
        ? t('analytics.cu.link.payerUnsetImport')
        : t('analytics.cu.link.payerUnsetExport')
    items.push({ key: 'payer', text, tone: 'neutral' })
  }

  // interval — та же величина и та же подпись, что в «Деталях кейса»
  const interval = buildPairInterval(detail, t)
  const d = daysText(interval.days, t)
  if (d) {
    items.push({
      key: 'days',
      text: interval.status === 'confirmed_by_pairing_dates'
        ? t('analytics.cu.link.intervalWithDates', { days: d, dates: interval.intervalDatesText ?? '' })
        : t('analytics.cu.link.interval', { days: d }),
      tone: 'match',
    })
  }
  if (interval.warning) {
    items.push({ key: 'days_warning', text: interval.warning, tone: 'neutral' })
  }

  // transport units — stated neutrally: a different unit is what scenarios 6–7 look for
  const wagonI = legText(imp, 'wagonNumber')
  const wagonE = legText(exp, 'wagonNumber')
  const contI = legText(imp, 'containerNumber')
  const contE = legText(exp, 'containerNumber')
  if (sameText(wagonI, wagonE)) {
    items.push({ key: 'unit', text: t('analytics.cu.link.sameWagon', { wagon: wagonI ?? '' }), tone: 'match' })
  } else if (wagonI && wagonE) {
    items.push({
      key: 'unit',
      text: t('analytics.cu.link.differentWagons', { from: wagonI, to: wagonE }),
      tone: 'neutral',
    })
  }
  if (sameText(contI, contE)) {
    items.push({ key: 'container', text: t('analytics.cu.link.sameContainer', { container: contI ?? '' }), tone: 'match' })
  } else if (contI && contE) {
    items.push({
      key: 'container',
      text: t('analytics.cu.link.differentContainers', { from: contI, to: contE }),
      tone: 'neutral',
    })
  }
  // Одна оговорка на обе транспортные единицы, и она НЕ утверждает перегрузку.
  // Прежняя формулировка («для сценария N это допустимо, так как проверяется
  // связь между разными транспортными единицами») читалась как подтверждение,
  // что груз физически переставили: сценарий лишь ДОПУСКАЕТ смену единицы, а
  // сама передача остаётся недоказанной, пока её не подтвердят документы.
  const wagonChanged = !!wagonI && !!wagonE && !sameText(wagonI, wagonE)
  const containerChanged = !!contI && !!contE && !sameText(contI, contE)
  if (wagonChanged || containerChanged) {
    items.push({ key: 'unit_note', text: differentUnitsNote(scenarioId, t), tone: 'neutral' })
  }

  return { items, strengthNote: matchStrengthNote(detail.pairing?.matchScore, t) }
}

/* ── 4. «Стыковка перевозок» ──────────────────────────────────────────────── */

/** Route as one line: origin → Kazakhstan → destination, with repeats collapsed. */
export function buildRouteText(detail: CandidatePairDetailData): string | null {
  const parts = [
    countryText(legText(detail.importLeg, 'departureCountry')),
    countryText(legText(detail.importLeg, 'destinationCountry')),
    countryText(legText(detail.exportLeg, 'departureCountry')),
    countryText(legText(detail.exportLeg, 'destinationCountry')),
  ].filter((x): x is string => !!x)
  const out: string[] = []
  for (const p of parts) if (out[out.length - 1] !== p) out.push(p)
  return out.length >= 2 ? out.join(' → ') : null
}

function weightText(leg: LegDetail | null | undefined, f: Formatter, t: TranslateFn): string | null {
  const w = legNumber(leg, 'weightTons')
  return w === null
    ? null
    : t('analytics.cu.leg.weightTons', { value: f.number(w, { maximumFractionDigits: 3 }) })
}

export function buildLegs(
  detail: CandidatePairDetailData,
  f: Formatter,
  t: TranslateFn,
): CrossUnitCaseView['legs'] {
  const imp = detail.importLeg
  const exp = detail.exportLeg

  const interval = buildPairInterval(detail, t)

  const importRows: CaseFact[] = []
  push(importRows, 'country', t('analytics.cu.leg.departureCountry'), countryText(legText(imp, 'departureCountry')))
  push(importRows, 'shipment', t('analytics.cu.leg.shipmentId'), legText(imp, 'shipmentId'))
  push(importRows, 'date', t('analytics.cu.leg.shipmentDate'), legText(imp, 'acceptanceDate') ? formatDay(legText(imp, 'acceptanceDate'), t) : null)
  // Дата раскредитования — это второе событие ноги, и именно от него считается
  // интервал стыковки. Без неё «5 дней» под датой отправки читается как ошибка.
  push(importRows, 'release', t('analytics.cu.leg.releaseDate'), legText(imp, 'releaseDate') ? formatDay(legText(imp, 'releaseDate'), t) : null)
  push(importRows, 'unit', t('analytics.cu.leg.unit'), legUnitText(imp, t))
  push(importRows, 'cargo', t('common.field.cargo'), legText(imp, 'cargoName'))
  push(importRows, 'station', t('analytics.cu.leg.destinationStationKz'), legText(imp, 'destinationStationName'))
  push(importRows, 'payer', t('analytics.cu.leg.payer'), payerText(readPayer(imp, 'import'), t))
  push(importRows, 'weight', t('analytics.cu.leg.weight'), weightText(imp, f, t))

  const exportRows: CaseFact[] = []
  push(exportRows, 'country', t('analytics.cu.leg.destinationCountry'), countryText(legText(exp, 'destinationCountry')))
  push(exportRows, 'shipment', t('analytics.cu.leg.shipmentId'), legText(exp, 'shipmentId'))
  push(exportRows, 'date', t('analytics.cu.leg.shipmentDate'), legText(exp, 'acceptanceDate') ? formatDay(legText(exp, 'acceptanceDate'), t) : null)
  push(exportRows, 'unit', t('analytics.cu.leg.unit'), legUnitText(exp, t))
  push(exportRows, 'cargo', t('common.field.cargo'), legText(exp, 'cargoName'))
  push(exportRows, 'station', t('analytics.cu.leg.departureStationKz'), legText(exp, 'departureStationName'))
  push(exportRows, 'payer', t('analytics.cu.leg.payer'), payerText(readPayer(exp, 'export'), t))
  push(exportRows, 'weight', t('analytics.cu.leg.weight'), weightText(exp, f, t))

  const middle: CaseFact[] = []
  push(middle, 'route', t('analytics.cu.middle.route'), buildRouteText(detail))
  const stIn = legText(imp, 'destinationStationName')
  const stOut = legText(exp, 'departureStationName')
  if (sameText(stIn, stOut)) push(middle, 'junction', t('analytics.cu.middle.junctionStation'), stIn)
  else if (stIn && stOut) push(middle, 'junction', t('analytics.cu.middle.junctionStations'), `${stIn} → ${stOut}`)
  if (interval.intervalDatesLabel) {
    push(middle, 'interval_dates', interval.intervalDatesLabel, interval.intervalDatesText)
  }
  push(middle, 'days', t('analytics.cu.middle.interval'), daysText(interval.days, t))
  push(middle, 'days_warning', t('analytics.cu.middle.intervalCheck'), interval.warning)
  // Shared formatter — the drawer can never state a weight relation the table contradicts.
  const w = formatWeightDifference({
    importWeight: legNumber(imp, 'weightTons'),
    exportWeight: legNumber(exp, 'weightTons'),
    weightDiffPct: null,
  })
  if (w.exactPercent !== null) push(middle, 'weight', t('analytics.cu.leg.weight'), w.detailText)

  return {
    importLeg: { title: t('analytics.cu.leg.importTitle'), rows: importRows },
    exportLeg: { title: t('analytics.cu.leg.exportTitle'), rows: exportRows },
    middle,
  }
}

/* ── 5. «Что проверить аналитику» ─────────────────────────────────────────── */

export function buildChecklist(detail: CandidatePairDetailData, t: TranslateFn): string[] {
  const imp = detail.importLeg
  const exp = detail.exportLeg
  const out: string[] = []

  out.push(t('analytics.cu.check.handoverDocs'))

  const wagonI = legText(imp, 'wagonNumber')
  const wagonE = legText(exp, 'wagonNumber')
  if (wagonI && wagonE && !sameText(wagonI, wagonE)) {
    out.push(t('analytics.cu.check.wagonChange', { from: wagonI, to: wagonE }))
  }
  const contI = legText(imp, 'containerNumber')
  const contE = legText(exp, 'containerNumber')
  if (contI && contE && !sameText(contI, contE)) {
    out.push(t('analytics.cu.check.containerChange', { from: contI, to: contE }))
  }

  const binIn = legText(imp, 'receiverBin')
  const binOut = legText(exp, 'senderBin')
  if (sameText(binIn, binOut)) out.push(t('analytics.cu.check.binLink', { bin: binIn ?? '' }))

  const payIn = readPayer(imp, 'import')
  const payOut = readPayer(exp, 'export')
  if (payIn.identified && payOut.identified) {
    if (sameText(payIn.code, payOut.code)) out.push(t('analytics.cu.check.samePayer', { code: payIn.code ?? '' }))
    else out.push(t('analytics.cu.check.differentPayers', { from: payIn.code ?? '', to: payOut.code ?? '' }))
  } else if (!payIn.identified && payIn.rawCode) {
    out.push(t('analytics.cu.check.identifyImportPayer', { code: payIn.rawCode }))
  } else if (!payOut.identified && payOut.rawCode) {
    out.push(t('analytics.cu.check.identifyExportPayer', { code: payOut.rawCode }))
  }

  // Одна и та же величина интервала во всех блоках — берётся из общего резолвера.
  const interval = buildPairInterval(detail, t)
  const d = daysText(interval.days, t)
  if (d) {
    out.push(interval.status === 'confirmed_by_pairing_dates'
      ? t('analytics.cu.check.intervalReasonWithDates', { days: d, dates: interval.intervalDatesText ?? '' })
      : t('analytics.cu.check.intervalReason', { days: d }))
  }
  if (interval.warning) out.push(t('analytics.cu.check.reconcileInterval', { warning: interval.warning }))

  const stIn = legText(imp, 'destinationStationName')
  const stOut = legText(exp, 'departureStationName')
  if (stIn && stOut && !sameText(stIn, stOut)) {
    out.push(t('analytics.cu.check.stationMismatch', { from: stIn, to: stOut }))
  }

  const w = formatWeightDifference({
    importWeight: legNumber(imp, 'weightTons'),
    exportWeight: legNumber(exp, 'weightTons'),
    weightDiffPct: null,
  })
  if (w.importWeightText && w.exportWeightText) {
    out.push(t('analytics.cu.check.reconcileWeight', { from: w.importWeightText, to: w.exportWeightText }))
  }

  return out
}

/* ── 6. «Детали кейса» ────────────────────────────────────────────────────── */

/**
 * Классификация груза обеих ног по замороженному справочнику ЕТСНГ.
 *
 * Коды берутся ровно теми же полями ног, что и строка «Груз» ниже, и нормализуются
 * канонически (ведущий ноль). Совпали — один общий блок; разошлись — классификация
 * каждой ноги отдельно, даже при одинаковой тарифной позиции: вывод «это один класс»
 * делает аналитик, а не карточка.
 */
export function buildCargoClassification(detail: CandidatePairDetailData): EtsngCaseClassification {
  return resolveCaseClassification(
    legText(detail.importLeg, 'cargoEtsng'),
    legText(detail.exportLeg, 'cargoEtsng'),
  )
}

export function buildDetails(
  detail: CandidatePairDetailData,
  scenarioId: number,
  t: TranslateFn,
): CaseFact[] {
  const imp = detail.importLeg
  const exp = detail.exportLeg
  const out: CaseFact[] = []
  const shortId = String(detail.identity?.canonicalPairId ?? '').slice(0, 8)

  push(out, 'case', t('analytics.cu.detail.caseScenario'),
    shortId ? t('analytics.cu.detail.caseScenarioValue', { id: shortId, scenario: scenarioId }) : null)

  const cargoName = legText(imp, 'cargoName') ?? legText(exp, 'cargoName')
  const etsng = legText(imp, 'cargoEtsng') ?? legText(exp, 'cargoEtsng')
  const gng = legText(imp, 'cargoGng') ?? legText(exp, 'cargoGng')
  const cargo = [cargoName, etsng ? `ЕТСНГ ${etsng}` : null, gng ? `ГНГ ${gng}` : null]
    .filter((x): x is string => !!x)
    .join(' · ')
  push(out, 'cargo', t('common.field.cargo'), cargo)

  push(out, 'route', t('common.field.route'), buildRouteText(detail))

  const shipI = legText(imp, 'shipmentId')
  const shipE = legText(exp, 'shipmentId')
  if (shipI && shipE) push(out, 'shipments', t('analytics.cu.detail.shipments'), `${shipI} / ${shipE}`)

  const wagonI = legText(imp, 'wagonNumber')
  const wagonE = legText(exp, 'wagonNumber')
  if (wagonI && wagonE) push(out, 'wagons', t('analytics.cu.detail.wagons'), sameText(wagonI, wagonE) ? wagonI : `${wagonI} → ${wagonE}`)
  const contI = legText(imp, 'containerNumber')
  const contE = legText(exp, 'containerNumber')
  if (contI && contE) push(out, 'containers', t('analytics.cu.detail.containers'), sameText(contI, contE) ? contI : `${contI} → ${contE}`)

  const stIn = legText(imp, 'destinationStationName')
  const stOut = legText(exp, 'departureStationName')
  if (sameText(stIn, stOut)) push(out, 'junction', t('analytics.cu.detail.junctionStation'), stIn)
  else if (stIn && stOut) push(out, 'junction', t('analytics.cu.detail.stations'), `${stIn} → ${stOut}`)

  // «Даты отправок» и «Интервал стыковки» — РАЗНЫЕ события, и подписаны они
  // так, чтобы одно не читалось основанием другого (см. `buildPairInterval`).
  const interval = buildPairInterval(detail, t)
  push(out, 'dates', t('analytics.cu.detail.shipmentDates'), interval.shipmentDatesText)
  if (interval.intervalDatesLabel) {
    push(out, 'interval_dates', interval.intervalDatesLabel, interval.intervalDatesText)
  }
  push(out, 'days', t('analytics.cu.detail.interval'), daysText(interval.days, t))

  const w = formatWeightDifference({
    importWeight: legNumber(imp, 'weightTons'),
    exportWeight: legNumber(exp, 'weightTons'),
    weightDiffPct: null,
  })
  if (w.exactPercent !== null) push(out, 'weight', t('analytics.cu.leg.weight'), w.detailText)

  const binIn = legText(imp, 'receiverBin')
  const binOut = legText(exp, 'senderBin')
  if (binIn && binOut) push(out, 'bin', t('common.field.bin'), sameText(binIn, binOut) ? `${binIn} → ${binOut}` : `${binIn} → ${binOut}`)

  // Плательщик — двумя строками с названием и кодом, а не одним «код → код»:
  // одна из сторон может быть не установлена, и это должно быть видно.
  const payIn = readPayer(imp, 'import')
  const payOut = readPayer(exp, 'export')
  if (payIn.rawCode || payIn.name) push(out, 'payer_import', t('analytics.cu.detail.payerImport'), payerText(payIn, t))
  if (payOut.rawCode || payOut.name) push(out, 'payer_export', t('analytics.cu.detail.payerExport'), payerText(payOut, t))
  if (payIn.identified && payOut.identified) {
    push(out, 'payer_same', t('analytics.cu.detail.payerSame'),
      sameText(payIn.code, payOut.code) ? t('analytics.cu.detail.yes') : t('analytics.cu.detail.no'))
  }

  return out
}

/* ── Stage 38: «Что повлияло на приоритет проверки» is gone ───────────────────
 *
 * That block rendered the ML priority model's SHAP factors as labels. It answered a
 * different question than the card asks («почему этот кейс необычен» is the anomaly
 * model's question, SHAP explains the priority model), and it was the one section on the
 * card that no longer earned its space.
 *
 * SHAP itself is untouched: the exact contributions, the base value and the model version
 * are read from the same payload fields and printed verbatim in «Технические сведения».
 * Nothing about the ML score, its ranking or its values changed.
 */

/* ── 7. «Технические сведения» ────────────────────────────────────────────── */

function shapLine(factors: { feature: string; contribution: number | null }[] | undefined): string | null {
  if (!factors?.length) return null
  return factors
    .map((f) => `${f.feature}=${typeof f.contribution === 'number' ? f.contribution.toFixed(4) : '—'}`)
    .join('; ')
}

export function buildTechnical(
  t: TranslateFn,
  detail: CandidatePairDetailData,
  explanations: CandidateExplanationsData | null | undefined,
  evidence?: AnomalyExplanationBlock | null,
): CaseFact[] {
  const out: CaseFact[] = []
  const p = detail.pairing
  const shap = explanations?.trackAShap ?? detail.explanations?.trackAShap
  const reasons = explanations?.anomalyReasons ?? detail.explanations?.anomalyReasons

  push(out, 'canonical_pair_id', 'canonical_pair_id', detail.identity?.canonicalPairId)
  push(out, 'stable_case_group_id', 'stable_case_group_id', detail.identity?.stableCaseGroupId)
  push(out, 'import_source_uid', 'import_source_uid', detail.identity?.importSourceUid)
  push(out, 'export_source_uid', 'export_source_uid', detail.identity?.exportSourceUid)
  push(out, 'catalog_version', 'catalog_version', detail.identity?.catalogVersion)
  push(out, 'track_a_model', 'track_a model_version', detail.trackA?.modelVersion)
  push(out, 'track_a_score', 'track_a score / level', `${detail.trackA?.displayedScore0to10 ?? '—'} / ${detail.trackA?.level ?? '—'}`)
  push(out, 'anomaly_model', 'anomaly model_version', detail.anomaly?.modelVersion)
  push(out, 'anomaly_score', 'anomaly score / level', `${detail.anomaly?.displayedScore0to10 ?? '—'} / ${detail.anomaly?.level ?? '—'}`)
  push(out, 'anomaly_percentile', 'anomaly frozen_percentile', detail.anomaly?.frozenPercentile?.toString())
  push(out, 'anomaly_cohort', 'anomaly cohort_id', detail.anomaly?.cohortId)
  push(out, 'reason_codes', 'anomaly reason_codes', reasons?.reasonCodes?.join('; '))
  push(out, 'reason_model', 'reason model_version', reasons?.modelVersion)
  push(out, 'shap_model', 'shap model_version', shap?.modelVersion)
  push(out, 'shap_base', 'shap base_value', typeof shap?.shapBaseValue === 'number' ? shap.shapBaseValue.toFixed(4) : null)
  push(out, 'shap_positive', 'shap top_positive', shapLine(shap?.topPositiveFactors))
  push(out, 'shap_negative', 'shap top_negative', shapLine(shap?.topNegativeFactors))
  push(out, 'match_score', 'match_score', typeof p?.matchScore === 'number' ? p.matchScore.toFixed(2) : null)
  push(out, 'pairing_status', 'pairing_status', p?.pairingStatus)
  push(out, 'ranks', 'import_rank / export_rank', `${p?.importRank ?? '—'} / ${p?.exportRank ?? '—'}`)
  push(
    out,
    'candidate_counts',
    'import_candidate_count / export_candidate_count',
    `${p?.importCandidateCount ?? '—'} / ${p?.exportCandidateCount ?? '—'}`,
  )
  push(
    out,
    'role',
    'cluster role',
    p?.isGroupRepresentative ? 'group_representative' : p?.isExistingSelected ? 'existing_selected_link' : 'alternative_not_confirmed',
  )
  push(out, 'representative_status', 'representative_status', detail.cluster?.representativeStatus)
  push(out, 'eligible_candidates', 'eligible_candidate_count', detail.cluster?.eligibleCandidateCount?.toString())
  push(out, 'limited_history', 'limited_history', detail.history?.limitedHistory ? 'true' : 'false')
  push(out, 'baseline_track_a', 'baseline track_a', detail.baselineDiagnostic?.trackA?.displayedScore0to10?.toFixed(4))
  push(out, 'baseline_anomaly', 'baseline anomaly', detail.baselineDiagnostic?.anomaly?.displayedScore0to10?.toFixed(4))
  // Stage 38 — the evidence record behind the reason cards, including the codes that
  // produced no card, so a silent omission stays visible to a reviewer.
  push(out, 'evidence_version', 'reason evidence version', evidence?.version)
  push(out, 'evidence_status', 'reason evidence status', evidence?.evidenceStatus)
  push(out, 'evidence_identity', 'reason evidence identity', evidence?.identityStatus)
  push(out, 'evidence_group', 'reason comparison group', evidence?.comparisonGroup)
  push(
    out,
    'evidence_population',
    'reason comparison population',
    evidence?.comparisonCount
      ? `${evidence.comparisonCount} (${evidence.historicalPeriodStart ?? '—'} … ${evidence.historicalPeriodEnd ?? '—'})`
      : null,
  )
  push(out, 'evidence_rule', 'reason rule', evidence?.reasonRule)
  push(
    out,
    'evidence_features',
    'reason evidence features',
    evidence?.reasons?.length
      ? evidence.reasons
          .map((r) => `${r.code}:${r.feature}=${r.observedValue} (median ${r.baselineValue}, z ${r.zScore?.toFixed(2)})`)
          .join('; ')
      : null,
  )
  push(
    out,
    'evidence_missing',
    'reason codes without evidence',
    evidence?.reasonCodesWithoutEvidence?.length ? evidence.reasonCodesWithoutEvidence.join('; ') : null,
  )
  push(out, 'evidence_source', 'reason evidence source', evidence?.evidenceSource)
  // Сырые коды плательщика обеих ног и сторона, по которой считается история
  // плательщика (Stage 24 ключует семейство `payer` на import_payer_code).
  const payIn = readPayer(detail.importLeg, 'import')
  const payOut = readPayer(detail.exportLeg, 'export')
  push(out, 'payer_import_raw', 'import payer_code / payer_name',
    `${payIn.rawCode ?? '—'} / ${payIn.name ?? '—'}${payIn.identified ? '' : ` ${t('analytics.cu.payer.notIdentifiedTech')}`}`)
  push(out, 'payer_export_raw', 'export payer_code / payer_name',
    `${payOut.rawCode ?? '—'} / ${payOut.name ?? '—'}${payOut.identified ? '' : ` ${t('analytics.cu.payer.notIdentifiedTech')}`}`)
  push(out, 'payer_history_side', 'payer history key', 'import_payer_code (Stage 24 build_history("payer"))')
  // Даты, между которыми сходится `days_between`, и сами даты отправок.
  const interval = buildPairInterval(detail, t)
  push(out, 'interval_source', 'days_between source',
    `${interval.days === null ? t('analytics.cu.tech.daysShortNoValue') : t('analytics.cu.tech.daysShort', { count: interval.days })} · ${interval.status}`)
  push(out, 'interval_dates_tech', 'interval dates', interval.intervalDatesText)
  push(out, 'shipment_dates_tech', 'shipment dates',
    interval.shipmentDatesText
      ? `${interval.shipmentDatesText}${interval.shipmentDiffDays === null ? '' : ` (${t('analytics.cu.tech.daysShort', { count: interval.shipmentDiffDays })})`}`
      : null)
  push(out, 'semantics', 'candidate semantics', candidateSemanticsTech(t))
  push(out, 'data_limitations', 'data_limitations', detail.dataLimitations?.join(' | '))

  // Per-field source status, so the hidden empty rows stay auditable.
  const missing = (leg: LegDetail | null | undefined) =>
    Object.entries(leg?.fieldStatus ?? {})
      .filter(([, v]) => v !== 'populated')
      .map(([k, v]) => `${k}=${v}`)
      .join('; ')
  push(out, 'import_field_status', 'import leg field_status', missing(detail.importLeg))
  push(out, 'export_field_status', 'export leg field_status', missing(detail.exportLeg))

  return out
}

/* ── the whole view ───────────────────────────────────────────────────────── */

export function buildCrossUnitCaseView(
  f: Formatter,
  t: TranslateFn,
  detail: CandidatePairDetailData,
  explanations: CandidateExplanationsData | null | undefined,
  fallbackScenarioId?: number,
): CrossUnitCaseView {
  const scenarioId = detail.scenario?.primaryDisplayScenarioId ?? fallbackScenarioId ?? 6
  const fullId = String(detail.identity?.canonicalPairId ?? '')
  const expl = explanations ?? detail.explanations

  const anomalyValue = scoreOutOfTen(detail.anomaly?.displayedScore0to10, t)
  const mlValue = scoreOutOfTen(detail.trackA?.displayedScore0to10, t)

  const evidence = detail.anomalyExplanation ?? null
  const subjects = buildReasonSubjects(detail, participantFamilyOf(evidence))
  const reasons = buildUnusualReasons(f, evidence, subjects)
  const interval = buildPairInterval(detail, t)

  return {
    header: {
      title: t('analytics.cu.header.title', { id: scenarioId }),
      shortId: fullId ? `#${fullId.slice(0, 8)}` : '',
      fullId,
      scenarioId,
    },
    notice: crossUnitNotice(t),
    attention: {
      anomaly: anomalyValue
        ? {
            label: t('analytics.cu.attention.anomalyLabel'),
            value: anomalyValue,
            levelLabel: anomalyLevelText(detail.anomaly?.level, t),
          }
        : null,
      ml: mlValue
        ? {
            label: t('analytics.cu.attention.mlLabel'),
            value: mlValue,
            levelLabel: mlLevelText(detail.trackA?.level, t),
          }
        : null,
      summary: buildAttentionSummary(detail.anomaly?.level ?? null, detail.trackA?.level ?? null, t),
    },
    unusual: {
      reasons,
      // The honest fallback, verbatim from the Stage 38 contract: a card with no numbers is
      // not replaced by a persuasive sentence.
      emptyNote: reasons.length ? null : NO_EVIDENCE_TEXT,
    },
    linkage: buildLinkage(detail, scenarioId, t),
    legs: buildLegs(detail, f, t),
    checklist: [...buildEvidenceChecklist(f, evidence, subjects), ...buildChecklist(detail, t)],
    details: buildDetails(detail, scenarioId, t),
    cargoClassification: buildCargoClassification(detail),
    dataQuality: interval.warning ? [interval.warning] : [],
    technical: buildTechnical(t, detail, expl, evidence),
  }
}

/* ── «Другие возможные связи» (collapsed) ─────────────────────────────────── */

export interface RelatedLinkRow {
  pairId: string
  shipments: string | null
  unit: string | null
  cargo: string | null
  route: string | null
  anomalyText: string | null
  mlText: string | null
  /** One short phrase saying how it differs from the link currently open. */
  difference: string | null
}

/**
 * A competing candidate, described in the same words as the case itself.
 *
 * Cluster ids, ranks and membership flags are deliberately absent — they were the reason
 * the old competition-cluster section read as pipeline diagnostics. They remain in the
 * technical block.
 */
export function buildRelatedLink(
  t: TranslateFn,
  item: {
    canonicalPairId: string
    importSummary?: { shipmentId?: string | null; wagonNumber?: string | null; containerNumber?: string | null; cargoName?: string | null; departureCountry?: string | null; destinationCountry?: string | null; weightTons?: number | null }
    exportSummary?: { shipmentId?: string | null; wagonNumber?: string | null; containerNumber?: string | null; cargoName?: string | null; departureCountry?: string | null; destinationCountry?: string | null; weightTons?: number | null }
    trackA?: { displayedScore0to10?: number | null }
    anomaly?: { displayedScore0to10?: number | null }
    pairing?: { daysBetween?: number | null }
  },
  current?: {
    importShipmentId?: string | null
    exportShipmentId?: string | null
    daysBetween?: number | null
  },
): RelatedLinkRow {
  const clean = (v: unknown): string | null => {
    if (v === null || v === undefined) return null
    const s = String(v).trim()
    return s === '' || s === '0' ? null : s
  }
  const impShip = clean(item.importSummary?.shipmentId)
  const expShip = clean(item.exportSummary?.shipmentId)
  const unitParts = [
    clean(item.importSummary?.wagonNumber) ?? clean(item.importSummary?.containerNumber),
    clean(item.exportSummary?.wagonNumber) ?? clean(item.exportSummary?.containerNumber),
  ].filter((x): x is string => !!x)
  const from = countryText(clean(item.importSummary?.departureCountry))
  const to = countryText(clean(item.exportSummary?.destinationCountry))

  const differences: string[] = []
  if (impShip && current?.importShipmentId && impShip !== current.importShipmentId) {
    differences.push(t('analytics.cu.related.otherImport'))
  }
  if (expShip && current?.exportShipmentId && expShip !== current.exportShipmentId) {
    differences.push(t('analytics.cu.related.otherExport'))
  }
  const d = typeof item.pairing?.daysBetween === 'number' ? item.pairing.daysBetween : null
  if (d !== null && typeof current?.daysBetween === 'number' && d !== current.daysBetween) {
    differences.push(t('analytics.cu.related.otherInterval', { days: daysText(d, t) ?? '' }))
  }

  const score = (v: number | null | undefined): string | null =>
    typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : null

  return {
    pairId: item.canonicalPairId,
    shipments: impShip && expShip ? `${impShip} / ${expShip}` : (impShip ?? expShip),
    unit: unitParts.length ? unitParts.join(' → ') : null,
    cargo: clean(item.importSummary?.cargoName) ?? clean(item.exportSummary?.cargoName),
    route: from && to ? `${from} → ${to}` : null,
    anomalyText: score(item.anomaly?.displayedScore0to10),
    mlText: score(item.trackA?.displayedScore0to10),
    difference: differences.length ? differences.join(', ') : null,
  }
}
